import { useEffect, useRef, useState } from "react";

/**
 * useReveal — sets a `revealed` flag the first time the element scrolls
 * into the viewport. Apply to any element to fade-up on entry.
 */
export function useReveal({ threshold = 0.18, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRevealed(true);
            io.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed, threshold, rootMargin]);

  return { ref, revealed };
}

/**
 * useCountUp — animates a number from 0 to `value` once the ref enters viewport.
 * Returns the current rendered string with thousand separators.
 */
export function useCountUp(value = 0, { duration = 1800, decimals = 0 } = {}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (started || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setStarted(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const elapsed = Math.min(1, (now - start) / duration);
      const v = value * ease(elapsed);
      setDisplay(
        v.toLocaleString("es-CO", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      );
      if (elapsed < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, value, duration, decimals]);

  return { ref, display };
}
